import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Trash2, RotateCw, Pencil } from 'lucide-react';

function ImageLayer({
  layer,
  isSelected = false,
  onSelect = () => {},
  onUpdate = () => {},
  onDelete = () => {},
  onEdit = () => {}, // Callback for edit submission: (layerId, prompt) => Promise<newImageUrl>
  isEditing = false, // Whether this layer is currently being edited (API call in progress)
  onTransformStart = () => {}, // Called when drag/resize/rotate starts
  onTransformComplete = () => {}, // Called when drag/resize/rotate finishes
  containerBounds = null, // Now this is the IMAGE bounds (actual rendered image rect)
  parentContainerBounds = null, // The parent container bounds (for calculating offset)
}) {
  // Get original image dimensions from layer or use defaults
  const originalImageWidth = layer.originalImageWidth || 1920;
  const originalImageHeight = layer.originalImageHeight || 1080;
  
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragStartPos, setDragStartPos] = useState(null);
  // NOTE: Resize uses refs/snapshots (not state) to avoid feedback loops and flicker.
  
  // Edit mode state
  const [showEditInput, setShowEditInput] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');
  
  const boxRef = useRef(null);
  const editInputRef = useRef(null);
  
  // Keep latest values in refs so global mouse handlers stay stable and don't get re-bound
  // on every `layer` update (which happens continuously during drag/resize/rotate).
  const layerRef = useRef(layer);
  const onUpdateRef = useRef(onUpdate);
  const onTransformStartRef = useRef(onTransformStart);
  const containerBoundsRef = useRef(containerBounds);
  const parentContainerBoundsRef = useRef(parentContainerBounds);
  const isDraggingRef = useRef(isDragging);
  const isResizingRef = useRef(isResizing);
  const isRotatingRef = useRef(isRotating);
  const dragOffsetRef = useRef(dragOffset);
  const dragStartPosRef = useRef(dragStartPos);
  const scaleFactorRef = useRef(1);
  const imageOffsetRef = useRef({ x: 0, y: 0 });
  
  // Snapshots captured at interaction start to avoid `getBoundingClientRect()` feedback loops.
  const resizeSnapshotRef = useRef(null);
  const rotateSnapshotRef = useRef(null);
  
  useEffect(() => { layerRef.current = layer; }, [layer]);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);
  useEffect(() => { onTransformStartRef.current = onTransformStart; }, [onTransformStart]);
  useEffect(() => { containerBoundsRef.current = containerBounds; }, [containerBounds]);
  useEffect(() => { parentContainerBoundsRef.current = parentContainerBounds; }, [parentContainerBounds]);
  useEffect(() => { isDraggingRef.current = isDragging; }, [isDragging]);
  useEffect(() => { isResizingRef.current = isResizing; }, [isResizing]);
  useEffect(() => { isRotatingRef.current = isRotating; }, [isRotating]);
  useEffect(() => { dragOffsetRef.current = dragOffset; }, [dragOffset]);
  useEffect(() => { dragStartPosRef.current = dragStartPos; }, [dragStartPos]);
  
  // Focus input when edit mode is shown
  useEffect(() => {
    if (showEditInput && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [showEditInput]);
  
  // Close edit input when layer is deselected
  useEffect(() => {
    if (!isSelected && showEditInput) {
      setShowEditInput(false);
      setEditPrompt('');
    }
  }, [isSelected, showEditInput]);
  
  // Handle edit button click
  const handleEditClick = useCallback((e) => {
    e.stopPropagation();
    setShowEditInput(true);
  }, []);
  
  // Handle edit submission
  const handleEditSubmit = useCallback(() => {
    if (!editPrompt.trim() || isEditing) return;
    onEdit(layer.id, editPrompt.trim());
    setShowEditInput(false);
    setEditPrompt('');
  }, [editPrompt, isEditing, layer.id, onEdit]);
  
  // Handle edit cancel
  const handleEditCancel = useCallback(() => {
    setShowEditInput(false);
    setEditPrompt('');
  }, []);

  // Calculate scale factor between original image and actual rendered image size
  const scaleFactor = useMemo(() => {
    if (!containerBounds) return 1;
    return containerBounds.width / originalImageWidth;
  }, [containerBounds, originalImageWidth]);

  // Calculate offset from parent container to image (for object-fit: contain centering)
  const imageOffset = useMemo(() => {
    if (!containerBounds || !parentContainerBounds) return { x: 0, y: 0 };
    return {
      x: containerBounds.left - parentContainerBounds.left,
      y: containerBounds.top - parentContainerBounds.top,
    };
  }, [containerBounds, parentContainerBounds]);
  
  useEffect(() => { scaleFactorRef.current = scaleFactor; }, [scaleFactor]);
  useEffect(() => { imageOffsetRef.current = imageOffset; }, [imageOffset]);

  // Convert original coordinates to screen coordinates (relative to parent container)
  const screenPosition = useMemo(() => ({
    x: imageOffset.x + (layer.position?.x || 0) * scaleFactor,
    y: imageOffset.y + (layer.position?.y || 0) * scaleFactor,
  }), [layer.position, scaleFactor, imageOffset]);

  const screenWidth = (layer.width || 100) * scaleFactor * (layer.scale || 1);
  const screenHeight = (layer.height || 100) * scaleFactor * (layer.scale || 1);

  // Check if we have crop info (for masked images from SAM-3)
  const hasCrop = layer.cropX !== undefined && layer.cropY !== undefined;

  // Handle mouse down for potential drag
  const handleMouseDown = useCallback((e) => {
    e.stopPropagation();
    
    const box = boxRef.current;
    if (!box) return;
    
    const rect = box.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setDragStartPos({ x: e.clientX, y: e.clientY });
    onSelect();
  }, [onSelect]);

  const handleMouseMove = useCallback((e) => {
    const bounds = containerBoundsRef.current;
    if (!bounds) return;

    // Check if we should start dragging (threshold)
    const startPos = dragStartPosRef.current;
    if (startPos && !isDraggingRef.current && !isResizingRef.current && !isRotatingRef.current) {
      const dx = e.clientX - startPos.x;
      const dy = e.clientY - startPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance > 5) {
        setIsDragging(true);
        isDraggingRef.current = true;
        onTransformStartRef.current('image', 'move');
      } else {
        return;
      }
    }

    if (!isDraggingRef.current && !isResizingRef.current && !isRotatingRef.current) return;

    const currentLayer = layerRef.current;
    const currentScaleFactor = scaleFactorRef.current || 1;

    if (isDraggingRef.current) {
      const off = dragOffsetRef.current || { x: 0, y: 0 };
      const snapThreshold = 6;

      // Calculate new screen position relative to the rendered image bounds
      let newScreenX = e.clientX - bounds.left - off.x;
      let newScreenY = e.clientY - bounds.top - off.y;

      const layerScale = currentLayer.scale || 1;
      const layerWidth = (currentLayer.width || 100) * currentScaleFactor * layerScale;
      const layerHeight = (currentLayer.height || 100) * currentScaleFactor * layerScale;
      const boundsCenterX = bounds.width / 2;
      const boundsCenterY = bounds.height / 2;

      const layerCenterX = newScreenX + layerWidth / 2;
      const layerCenterY = newScreenY + layerHeight / 2;

      if (Math.abs(layerCenterX - boundsCenterX) <= snapThreshold) {
        newScreenX = boundsCenterX - layerWidth / 2;
      }

      if (Math.abs(layerCenterY - boundsCenterY) <= snapThreshold) {
        newScreenY = boundsCenterY - layerHeight / 2;
      }

      const clampedScreenX = Math.max(0, Math.min(newScreenX, bounds.width - layerWidth));
      const clampedScreenY = Math.max(0, Math.min(newScreenY, bounds.height - layerHeight));

      // Convert back to original coordinates
      const newX = currentScaleFactor > 0 ? clampedScreenX / currentScaleFactor : 0;
      const newY = currentScaleFactor > 0 ? clampedScreenY / currentScaleFactor : 0;

      onUpdateRef.current({
        ...currentLayer,
        position: { x: newX, y: newY },
      });
    }

    if (isResizingRef.current && resizeSnapshotRef.current) {
      const snap = resizeSnapshotRef.current;

      // Pointer relative to parent container
      const pointerRelX = e.clientX - snap.parentLeft;
      const pointerRelY = e.clientY - snap.parentTop;

      // Distance to the fixed center captured on pointer down
      const distToMouse = Math.sqrt((pointerRelX - snap.centerRelX) ** 2 + (pointerRelY - snap.centerRelY) ** 2);
      const ratio = snap.startDist > 0 ? (distToMouse / snap.startDist) : 1;

      // Uniform scale with clamping
      const unclampedScale = snap.startScale * ratio;
      const newScale = Math.max(0.1, Math.min(5, unclampedScale));
      const scaleRatio = snap.startScale > 0 ? (newScale / snap.startScale) : 1;

      // Keep center fixed by adjusting position as size changes
      const newScreenWidth = snap.startBoxWidth * scaleRatio;
      const newScreenHeight = snap.startBoxHeight * scaleRatio;
      const newLeftRel = snap.centerRelX - newScreenWidth / 2;
      const newTopRel = snap.centerRelY - newScreenHeight / 2;

      const newX = snap.scaleFactor > 0 ? (newLeftRel - snap.imageOffsetX) / snap.scaleFactor : 0;
      const newY = snap.scaleFactor > 0 ? (newTopRel - snap.imageOffsetY) / snap.scaleFactor : 0;

      onUpdateRef.current({
        ...currentLayer,
        scale: newScale,
        position: { x: newX, y: newY },
      });
    }

    if (isRotatingRef.current && rotateSnapshotRef.current) {
      const snap = rotateSnapshotRef.current;
      const pointerRelX = e.clientX - snap.parentLeft;
      const pointerRelY = e.clientY - snap.parentTop;
      const angle = Math.atan2(pointerRelY - snap.centerRelY, pointerRelX - snap.centerRelX) * (180 / Math.PI);
      const newRotation = snap.startRotation + (angle - snap.startAngle);

      onUpdateRef.current({
        ...currentLayer,
        rotation: newRotation,
      });
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    const wasDragging = isDraggingRef.current;
    const wasResizing = isResizingRef.current;
    const wasRotating = isRotatingRef.current;
    
    setIsDragging(false);
    setIsResizing(false);
    setIsRotating(false);
    setDragStartPos(null);
    isDraggingRef.current = false;
    isResizingRef.current = false;
    isRotatingRef.current = false;
    resizeSnapshotRef.current = null;
    rotateSnapshotRef.current = null;
    
    // Notify parent when transform completes
    if (wasDragging) {
      onTransformComplete('image', 'move');
    } else if (wasResizing) {
      onTransformComplete('image', 'resize');
    } else if (wasRotating) {
      onTransformComplete('image', 'rotate');
    }
  }, [onTransformComplete]);

  // Attach global mouse events
  useEffect(() => {
    if (dragStartPos || isDragging || isResizing || isRotating) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragStartPos, isDragging, isResizing, isRotating, handleMouseMove, handleMouseUp]);

  // Handle resize handle mousedown
  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const box = boxRef.current;
    if (!box) return;
    
    const bounds = containerBoundsRef.current;
    const parentBounds = parentContainerBoundsRef.current || bounds;
    if (!bounds || !parentBounds) return;

    const rect = box.getBoundingClientRect();
    const centerRelX = (rect.left - parentBounds.left) + rect.width / 2;
    const centerRelY = (rect.top - parentBounds.top) + rect.height / 2;

    const pointerRelX = e.clientX - parentBounds.left;
    const pointerRelY = e.clientY - parentBounds.top;
    const dist = Math.sqrt((pointerRelX - centerRelX) ** 2 + (pointerRelY - centerRelY) ** 2);

    const currentLayer = layerRef.current;
    const sf = scaleFactorRef.current || 1;
    const off = imageOffsetRef.current || { x: 0, y: 0 };

    resizeSnapshotRef.current = {
      parentLeft: parentBounds.left,
      parentTop: parentBounds.top,
      centerRelX,
      centerRelY,
      startDist: dist,
      startScale: currentLayer.scale || 1,
      startBoxWidth: rect.width,
      startBoxHeight: rect.height,
      scaleFactor: sf,
      imageOffsetX: off.x || 0,
      imageOffsetY: off.y || 0,
    };

    setIsResizing(true);
    isResizingRef.current = true;
    onSelect();
  }, [onSelect]);

  // Handle rotate mousedown
  const handleRotateMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const box = boxRef.current;
    if (!box) return;
    
    const bounds = containerBoundsRef.current;
    const parentBounds = parentContainerBoundsRef.current || bounds;
    if (!bounds || !parentBounds) return;

    const rect = box.getBoundingClientRect();
    const centerRelX = (rect.left - parentBounds.left) + rect.width / 2;
    const centerRelY = (rect.top - parentBounds.top) + rect.height / 2;

    const pointerRelX = e.clientX - parentBounds.left;
    const pointerRelY = e.clientY - parentBounds.top;
    const angle = Math.atan2(pointerRelY - centerRelY, pointerRelX - centerRelX) * (180 / Math.PI);

    const currentLayer = layerRef.current;
    rotateSnapshotRef.current = {
      parentLeft: parentBounds.left,
      parentTop: parentBounds.top,
      centerRelX,
      centerRelY,
      startAngle: angle,
      startRotation: currentLayer.rotation || 0,
    };

    setIsRotating(true);
    isRotatingRef.current = true;
    onSelect();
  }, [onSelect]);

  // Build the style for the image content (either as background or as img)
  const contentStyle = useMemo(() => {
    if (!hasCrop) {
      // No crop - simple case
      return null;
    }
    
    // For cropped images, use background-image with positioning
    const bgSizeX = originalImageWidth * scaleFactor * (layer.scale || 1);
    const bgSizeY = originalImageHeight * scaleFactor * (layer.scale || 1);
    const bgPosX = -(layer.cropX || 0) * scaleFactor * (layer.scale || 1);
    const bgPosY = -(layer.cropY || 0) * scaleFactor * (layer.scale || 1);
    
    return {
      backgroundImage: `url(${layer.imageUrl})`,
      backgroundSize: `${bgSizeX}px ${bgSizeY}px`,
      backgroundPosition: `${bgPosX}px ${bgPosY}px`,
      backgroundRepeat: 'no-repeat',
    };
  }, [hasCrop, originalImageWidth, originalImageHeight, scaleFactor, layer.scale, layer.cropX, layer.cropY, layer.imageUrl]);

  return (
    <div
      ref={boxRef}
      className={`image-layer ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
      style={{
        left: screenPosition.x,
        top: screenPosition.y,
        width: screenWidth,
        height: screenHeight,
        transform: `rotate(${layer.rotation || 0}deg)`,
        ...(hasCrop ? contentStyle : {}),
      }}
      onMouseDown={handleMouseDown}
      onClick={(e) => {
        e.stopPropagation();
        if (!isSelected) onSelect();
      }}
    >
      {/* Only render img if not using crop (background-image) */}
      {!hasCrop && (
        <img
          src={layer.imageUrl}
          alt="Layer"
          draggable={false}
          className="image-layer-img"
        />
      )}

      {/* In-progress overlay */}
      {isEditing && (
        <div className="image-layer-editing-overlay" aria-hidden="true">
          <div className="image-layer-editing-glare" />
          <div className="image-layer-editing-label">Editing…</div>
        </div>
      )}
      
      {/* Selection border and handles */}
      {isSelected && (
        <>
          {/* Corner resize handles */}
          <div 
            className="image-layer-handle image-layer-handle-nw"
            onMouseDown={handleResizeMouseDown}
          />
          <div 
            className="image-layer-handle image-layer-handle-ne"
            onMouseDown={handleResizeMouseDown}
          />
          <div 
            className="image-layer-handle image-layer-handle-sw"
            onMouseDown={handleResizeMouseDown}
          />
          <div 
            className="image-layer-handle image-layer-handle-se"
            onMouseDown={handleResizeMouseDown}
          />
          
          {/* Rotation handle */}
          <div 
            className="image-layer-rotate-handle"
            onMouseDown={handleRotateMouseDown}
            title="Rotate"
          >
            <RotateCw size={12} />
          </div>

          {/* Action stack (prevents overlap on small layers) */}
          <div className="image-layer-action-stack">
            <button
              className="image-layer-edit"
              onClick={handleEditClick}
              title="Edit layer"
              disabled={isEditing}
            >
              <Pencil size={14} />
            </button>

            <button
              className="image-layer-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(layer.id);
              }}
              title="Delete layer"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </>
      )}
      
      {/* Inline edit input */}
      {showEditInput && (
        <div className="image-layer-edit-container" onClick={(e) => e.stopPropagation()}>
          <input
            ref={editInputRef}
            type="text"
            className="image-layer-edit-input"
            placeholder="Describe the edit..."
            value={editPrompt}
            onChange={(e) => setEditPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && editPrompt.trim()) {
                handleEditSubmit();
              } else if (e.key === 'Escape') {
                handleEditCancel();
              }
            }}
            disabled={isEditing}
          />
          <div className="image-layer-edit-actions">
            <button 
              className="image-layer-edit-submit"
              onClick={handleEditSubmit}
              disabled={!editPrompt.trim() || isEditing}
            >
              {isEditing ? 'Editing...' : 'Apply'}
            </button>
            <button 
              className="image-layer-edit-cancel"
              onClick={handleEditCancel}
              disabled={isEditing}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        .image-layer {
          position: absolute;
          cursor: move;
          user-select: none;
          border: 2px solid transparent;
          border-radius: 4px;
          transition: border-color 0.15s ease;
          z-index: 15;
        }

        .image-layer:hover {
          border-color: var(--color-border-input);
        }

        .image-layer.selected {
          border-color: var(--color-text-secondary);
          box-shadow: 0 0 0 1px var(--color-border-input);
        }

        .image-layer.dragging {
          opacity: 0.8;
          cursor: grabbing;
        }

        .image-layer-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          pointer-events: none;
        }

        .image-layer-editing-overlay {
          position: absolute;
          inset: 0;
          pointer-events: none;
          border-radius: 4px;
          overflow: hidden;
          z-index: 9;
          background: rgba(15, 14, 12, 0.2);
          box-shadow: 0 0 0 1px var(--color-border-input) inset;
          animation: imageLayerEditingPulse 0.9s ease-in-out infinite;
        }

        .image-layer-editing-glare {
          position: absolute;
          top: -50%;
          left: -60%;
          width: 60%;
          height: 200%;
          transform: rotate(18deg);
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.4) 50%,
            rgba(255, 255, 255, 0) 100%
          );
          filter: blur(0.5px);
          animation: imageLayerEditingGlare 1.2s linear infinite;
        }

        .image-layer-editing-label {
          position: absolute;
          bottom: 6px;
          left: 6px;
          padding: 4px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.2px;
          color: var(--color-text-primary);
          background: var(--color-bg-canvas-surface);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        @keyframes imageLayerEditingGlare {
          0% {
            left: -70%;
          }
          100% {
            left: 120%;
          }
        }

        @keyframes imageLayerEditingPulse {
          0% {
            opacity: 0.9;
          }
          50% {
            opacity: 1;
          }
          100% {
            opacity: 0.9;
          }
        }

        .image-layer-handle {
          position: absolute;
          width: 10px;
          height: 10px;
          background: var(--color-bg-canvas-surface);
          border: 2px solid var(--color-text-secondary);
          border-radius: 2px;
          z-index: 10;
        }

        .image-layer-handle-nw {
          top: -5px;
          left: -5px;
          cursor: nwse-resize;
        }

        .image-layer-handle-ne {
          top: -5px;
          right: -5px;
          cursor: nesw-resize;
        }

        .image-layer-handle-sw {
          bottom: -5px;
          left: -5px;
          cursor: nesw-resize;
        }

        .image-layer-handle-se {
          bottom: -5px;
          right: -5px;
          cursor: nwse-resize;
        }

        .image-layer-rotate-handle {
          position: absolute;
          top: -30px;
          left: 50%;
          transform: translateX(-50%);
          width: 24px;
          height: 24px;
          background: var(--color-bg-canvas-surface);
          border: 2px solid var(--color-text-secondary);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: grab;
          color: var(--color-text-secondary);
          z-index: 10;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
        }

        .image-layer-rotate-handle:hover {
          background: var(--color-bg-input);
          color: var(--color-text-primary);
        }

        .image-layer-rotate-handle:active {
          cursor: grabbing;
        }

        .image-layer-action-stack {
          position: absolute;
          top: -58px;
          right: -10px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          z-index: 12;
          pointer-events: auto;
        }

        .image-layer-edit {
          position: relative;
          width: 24px;
          height: 24px;
          padding: 0;
          background: var(--color-bg-canvas-surface);
          border: 1px solid var(--color-border-input);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: var(--color-text-secondary);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          transition: all 0.15s ease;
        }

        .image-layer-edit:hover:not(:disabled) {
          background: var(--color-bg-input);
          color: var(--color-text-primary);
          border-color: var(--color-text-secondary);
        }

        .image-layer-edit:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .image-layer-delete {
          position: relative;
          width: 24px;
          height: 24px;
          padding: 0;
          background: var(--color-bg-canvas-surface);
          border: 1px solid rgba(220, 38, 38, 0.3);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #dc2626;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          transition: all 0.15s ease;
        }

        .image-layer-delete:hover {
          background: rgba(220, 38, 38, 0.12);
          color: #dc2626;
          border-color: rgba(220, 38, 38, 0.45);
        }

        /* Inline edit container - light theme */
        .image-layer-edit-container {
          position: absolute;
          bottom: -60px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 8px;
          z-index: 300;
          padding: 8px 12px;
          background: var(--color-bg-canvas-surface);
          backdrop-filter: blur(8px);
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25),
                      0 0 0 1px var(--color-border-input);
          animation: imageLayerEditAppear 0.25s ease-out;
        }

        @keyframes imageLayerEditAppear {
          0% {
            opacity: 0;
            transform: translateX(-50%) translateY(10px);
          }
          100% {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }

        .image-layer-edit-input {
          width: 200px;
          padding: 10px 14px;
          border: 1px solid var(--color-border-input);
          border-radius: 8px;
          background: var(--color-bg-input);
          font-size: 14px;
          font-family: inherit;
          color: var(--color-text-primary);
          transition: all 0.2s ease;
        }

        .image-layer-edit-input:focus {
          outline: none;
          border-color: var(--color-text-secondary);
          background: var(--color-bg-canvas-surface);
        }

        .image-layer-edit-input::placeholder {
          color: var(--color-text-secondary);
        }

        .image-layer-edit-input:disabled {
          opacity: 0.6;
        }

        .image-layer-edit-actions {
          display: flex;
          gap: 6px;
        }

        .image-layer-edit-submit,
        .image-layer-edit-cancel {
          padding: 10px 16px;
          border: none;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .image-layer-edit-submit {
          background: var(--color-bg-input);
          color: var(--color-text-primary);
          border: 1px solid var(--color-border-input);
        }

        .image-layer-edit-submit:hover:not(:disabled) {
          background: var(--color-bg-user-bubble);
        }

        .image-layer-edit-submit:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .image-layer-edit-cancel {
          background: transparent;
          border: 1px solid var(--color-border-input);
          color: var(--color-text-secondary);
        }

        .image-layer-edit-cancel:hover:not(:disabled) {
          background: var(--color-bg-input);
          color: var(--color-text-primary);
          border-color: var(--color-border-input);
        }

        .image-layer-edit-cancel:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}

export default ImageLayer;
